import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Zap, BarChart3, Trophy, ChevronDown, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import RestrictedAccessModal from "./RestrictedAccessModal";

const HomeNavigation = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [restrictedModal, setRestrictedModal] = useState({ open: false, featureName: '' });
  const isActive = (path: string) => location.pathname === path;

  const handleRestrictedAccess = (featureName: string, path: string, event: React.MouseEvent) => {
    if (!isAuthenticated) {
      event.preventDefault();
      setRestrictedModal({ open: true, featureName });
    }
  };

  return (
    <>
      <nav className="hidden md:flex items-center space-x-2 relative"
           style={{ pointerEvents: 'auto' }}>
        {/* Início */}
        <Link to="/">
          <Button 
            variant={isActive("/") ? "secondary" : "ghost"} 
            className="h-10"
          >
            Início
          </Button>
        </Link>

        {/* Gerar Jogos IA */}
        <div className="relative group">
          <Button 
            variant="ghost" 
            className="h-10 bg-transparent hover:bg-accent hover:text-accent-foreground group-hover:bg-accent/50"
          >
            <Zap className="mr-2 h-4 w-4" />
            Gerar Jogos IA
            <ChevronDown className="ml-1 h-4 w-4" />
          </Button>
          
          {/* Dropdown */}
          <div className="absolute left-0 top-full mt-1 w-[400px] bg-background border border-border rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
            <div className="p-4 space-y-3">
              <Link 
                to="/gerar-jogos" 
                onClick={(e) => handleRestrictedAccess("Gerar Jogos IA", "/gerar-jogos", e)}
                className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
              >
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  <div className="text-sm font-medium leading-none">Gerar Jogos IA</div>
                </div>
                <p className="text-sm leading-snug text-muted-foreground">
                  Gere jogos inteligentes para o jogo do bicho
                </p>
              </Link>
              
              <Link 
                to="/software" 
                onClick={(e) => handleRestrictedAccess("Software Turbinado", "/software", e)}
                className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
              >
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4" />
                  <div className="text-sm font-medium leading-none">Soft. Turb. IA</div>
                </div>
                <p className="text-sm leading-snug text-muted-foreground">
                  Software turbinado com inteligência artificial
                </p>
              </Link>

              <Link 
                to="/loterias" 
                onClick={(e) => handleRestrictedAccess("Loterias Caixa", "/loterias", e)}
                className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
              >
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4" />
                  <div className="text-sm font-medium leading-none">Loterias Caixa</div>
                </div>
                <p className="text-sm leading-snug text-muted-foreground">
                  Palpites para loterias da Caixa Econômica
                </p>
              </Link>
            </div>
          </div>
        </div>

        {/* Métodos */}
        <div className="relative group">
          <Button 
            variant="ghost" 
            className="h-10 bg-transparent hover:bg-accent hover:text-accent-foreground group-hover:bg-accent/50"
          >
            🎯
            Métodos
            <ChevronDown className="ml-1 h-4 w-4" />
          </Button>
          
          {/* Dropdown */}
          <div className="absolute left-0 top-full mt-1 w-[450px] bg-background border border-border rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
            <div className="p-4 space-y-3">
              <Link 
                to="/metodos" 
                onClick={(e) => handleRestrictedAccess("Métodos IA", "/metodos", e)}
                className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
              >
                <div className="flex items-center gap-2">
                  🎯
                  <div className="text-sm font-medium leading-none">Métodos c/ IA</div>
                </div>
                <p className="text-sm leading-snug text-muted-foreground">
                  Métodos exclusivos com inteligência artificial
                </p>
              </Link>
              
              <div className="grid grid-cols-2 gap-2">
                <Link 
                  to="/metodos?method=bio-numerico" 
                  onClick={(e) => handleRestrictedAccess("Bio Numérico Stellar", "/metodos?method=bio-numerico", e)}
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="text-sm font-medium leading-none">Bio Numérico Stellar</div>
                </Link>
                
                <Link 
                  to="/metodos?method=centena-bichos" 
                  onClick={(e) => handleRestrictedAccess("Centena dos Bichos", "/metodos?method=centena-bichos", e)}
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="text-sm font-medium leading-none">Centena dos Bichos</div>
                </Link>
                
                <Link 
                  to="/metodos?method=centenas-viciadas" 
                  onClick={(e) => handleRestrictedAccess("Centenas Viciadas", "/metodos?method=centenas-viciadas", e)}
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="text-sm font-medium leading-none">Centenas Viciadas</div>
                </Link>
                
                <Link 
                  to="/metodos?method=dezenas-premiadas" 
                  onClick={(e) => handleRestrictedAccess("Dezenas Premiadas", "/metodos?method=dezenas-premiadas", e)}
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="text-sm font-medium leading-none">Dezenas Premiadas</div>
                </Link>
                
                <Link 
                  to="/metodos?method=esquema-infalivel" 
                  onClick={(e) => handleRestrictedAccess("Esquema Infalível", "/metodos?method=esquema-infalivel", e)}
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="text-sm font-medium leading-none">Esquema Infalível</div>
                </Link>
                
                <Link 
                  to="/metodos?method=metodo-777" 
                  onClick={(e) => handleRestrictedAccess("Método 777", "/metodos?method=metodo-777", e)}
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="text-sm font-medium leading-none">Método 777</div>
                </Link>
                
                <Link 
                  to="/metodos?method=cruz-dia" 
                  onClick={(e) => handleRestrictedAccess("Cruz do Dia", "/metodos?method=cruz-dia", e)}
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="text-sm font-medium leading-none">Cruz do Dia</div>
                </Link>
                
                <Link 
                  to="/metodos?method=sequencia" 
                  onClick={(e) => handleRestrictedAccess("Método Sequência", "/metodos?method=sequencia", e)}
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="text-sm font-medium leading-none">Método Sequência</div>
                </Link>
                
                <Link 
                  to="/metodos?method=soma-chave-ouro" 
                  onClick={(e) => handleRestrictedAccess("Soma Chave de Ouro", "/metodos?method=soma-chave-ouro", e)}
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="text-sm font-medium leading-none">Soma Chave de Ouro</div>
                </Link>
                
                <Link 
                  to="/metodos?method=calculadora-chave-ouro" 
                  onClick={(e) => handleRestrictedAccess("Calculadora Chave de Ouro", "/metodos?method=calculadora-chave-ouro", e)}
                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="text-sm font-medium leading-none">Calculadora Chave de Ouro</div>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Resultados */}
        <div className="relative group">
          <Button 
            variant="ghost" 
            className="h-10 bg-transparent hover:bg-accent hover:text-accent-foreground group-hover:bg-accent/50"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Resultados
            <ChevronDown className="ml-1 h-4 w-4" />
          </Button>
          
          {/* Dropdown */}
          <div className="absolute left-0 top-full mt-1 w-[350px] bg-background border border-border rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
            <div className="p-4 space-y-3">
              <Link 
                to="/resultados-bicho" 
                className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
              >
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  <div className="text-sm font-medium leading-none">Resultado RIO</div>
                </div>
                <p className="text-sm leading-snug text-muted-foreground">
                  Resultados do jogo do bicho do Rio de Janeiro
                </p>
              </Link>
              
              <Link 
                to="/loterias" 
                onClick={(e) => handleRestrictedAccess("Loterias Caixa", "/loterias", e)}
                className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
              >
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4" />
                  <div className="text-sm font-medium leading-none">Loterias Caixa</div>
                </div>
                <p className="text-sm leading-snug text-muted-foreground">
                  Resultados das loterias da Caixa
                </p>
              </Link>

              <Link 
                to="/estatisticas" 
                className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
              >
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  <div className="text-sm font-medium leading-none">Estatísticas</div>
                </div>
                <p className="text-sm leading-snug text-muted-foreground">
                  Análise estatística dos resultados
                </p>
              </Link>
            </div>
          </div>
        </div>

        {/* Estatísticas */}
        <Link to="/estatisticas">
          <Button 
            variant="ghost" 
            className="h-10 estatisticas-destaque"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Estatísticas
          </Button>
        </Link>

        {/* Loja */}
        <Link to="/loja">
          <Button 
            variant="ghost" 
            className="h-10 loja-vibrante"
          >
            <ShoppingBag className="mr-2 h-4 w-4" />
            Loja
          </Button>
        </Link>

        {/* Pagamento PIX */}
        <Link to="/pagamento">
          <Button 
            variant={isActive("/pagamento") ? "secondary" : "ghost"} 
            className="h-10"
          >
            💳 Pagamento
          </Button>
        </Link>
      </nav>

      <RestrictedAccessModal
        open={restrictedModal.open}
        onClose={() => setRestrictedModal({ open: false, featureName: '' })}
        featureName={restrictedModal.featureName}
      />
    </>
  );
};

export default HomeNavigation;